import _ from 'lodash';
import ColorScale from 'color-scales';

import { ALPHABET, ANOMALY_SUMMARY_OPTIONS, ANOMALY_TYPE, DAMAGE_CLASS, USER_TYPES } from './constants';
import { formatNumberRange, getAnomalyClassStyle, getAnomalyTypeStyle, getItemStyle, getNumberOfMeasurementsForPECScan, getPCNLAnomalyStyle, getStyleFromRisk, getValueRange, getValueToDecimalPlaces, matchPhrases } from './utilities';
import {
	Column,
	CoverDataOverview,
	CustomLPTItem,
	ETSummarySheetData,
	GeneralInformationData,
	HelideckSummaryOverview,
	InspectionResultItem,
	RiskLevel,
	RiskRatingItem,
	SelectOption,
	VIReportItem,
	VTEquipmentAnomalyData,
	VTAnomalyData,
	VTStructureAnomalyData,
	HelideckVIItem,
	PECScanItem,
	MUTUTInspectionItem,
	ETIReportItem,
	PAUTWeldScanItem,
	GridScanData,
	EquipmentAnomalyItem,
	AnomalyItem,
	CustomLPTReportItem,
	NewAnomalyItem,
	UTMInspection,
	ConcreteHammerReportItem,
	PCNLAnomalyItem,
	PCNLRiskAssessmentItem,
} from './models';

const getAffirmativeOptions = (): SelectOption[] => {
	return [
		{
			id: 'yes',
			name: 'Yes',
		},
		{
			id: 'no',
			name: 'No',
		},
	];
};

const getAnomalySummaryOptions = (): SelectOption[] => {
	return [
		{
			id: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
			name: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		},
		{
			id: ANOMALY_SUMMARY_OPTIONS.ANOMALY,
			name: ANOMALY_SUMMARY_OPTIONS.ANOMALY,
		},
	];
};

const getInspectionTypeOptions = () => {
	return [
		{
			id: 'internal',
			name: 'Internal',		
		},
		{
			id: 'external',
			name: 'External',		
		},
	];
};

const getNoAnomalySummaryOptions = (): SelectOption[] => {
	return [
		{
			id: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
			name: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		},
	]
}

const getPittingAnomalySummaryOptions = (): SelectOption[] => {
	return [
		{
			id: 'No Pitting',
			name: 'No Pitting',
		},
		{
			id: 'Light Pitting',
			name: 'Light Pitting',
		},
		{
			id: 'Moderate Pitting',
			name: 'Moderate Pitting',
		},
		{
			id: 'Pitting Corrosion',
			name: 'Pitting Corrosion',
		},
		{
			id: 'Passive Pitting',
			name: 'Passive Pitting',
		},
		{
			id: 'Active Pitting',
			name: 'Active Pitting',
		},
	];
};

const getPittingNoAnomalySummaryOptions = (): SelectOption[] => {
	return [
		{
			id: 'No Pitting',
			name: 'No Pitting',
		},
	];
};

const getAverageScanThickness = (item: PECScanItem) => {
	return (item.minimumThickness + item.maximumThickness) / 2;
};

const getColorByScore = (score: number): string => {
	if (score > 7) return 'green';
	if (score > 5) return '#ffff0f';
	return 'red';
};

const getResultItemStyle = (item: ETIReportItem): React.CSSProperties => {
	const style: React.CSSProperties = { 
		backgroundColor: 'red', 
		color: '#ffffff', 
		textAlign: 'center', 
		textTransform: 'capitalize' 
	};
	
	const option = getResultOptions().concat(getResultOptionsForDRTReport()).find((option) => matchPhrases(option.id, item.result));

	if (option) {
		style.backgroundColor = option.color;
	}

	if (matchPhrases(item.result, 'accept')) {
		style.backgroundColor = 'green';
	}

	return style;
};

const getETTestingColumns = (): Column[] => {
	return [
		{
			path: 'srNo',
			label: 'SR/NO',
		},
		{
			path: 'description',
			label: 'Description',
			style: () => ({ textAlign: 'left' }),
		},
		{
			path: 'result',
			label: 'Result',
			style: (item) => getResultItemStyle(item),
		},
		{
			path: 'images',
			label: 'Photo Ref',
			content: (item: ETIReportItem) => (
				<span>{formatNumberRange(item.images.map((i) => +i.value))}</span>
			),
		},
	];
};

const getMUTItemStyle = (item: MUTUTInspectionItem): any => {
	let backgroundColor: string = getColorByScore(+item.a);
	return { backgroundColor };
};

const getAUTScanColumns = (): Column[] => {
	return [
		{
			path: '',
			label: 'S/N',
			content: (i: GridScanData) => <span>{i.id}</span>,
		},
		{
			path: 'description',
			label: 'Description',
			style: (i: GridScanData) => ({ backgroundColor: '#ffff0f' }),
		},
		{ path: 'size', label: 'Size (in)' },
		{
			path: 'grid',
			label: '',
			style: (i: GridScanData) => ({ backgroundColor: '#dfe1e6' }),
		},
	];
};

const getExxonMobilAUTSummaryColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
		},
		{
			path: 'scanSectionDetails',
			label: 'Scan Section Details',
		},
		{ path: 'result', label: 'Result' },
		{ path: 'scanNumber', label: 'Scan / UTG Number' },
	];
};

const getAUTWeldColumns = (): Column[] => {
	return [
		{
			path: 'description',
			label: 'Description',
		},
		{
			path: 'finding',
			label: 'Finding',
		},
		{
			path: 'result',
			label: 'Result',
		},
	];
};

const getMUTColumns = (): Column[] => {
	return [
		{
			path: 'tmlLocation',
			label: 'TML Location',
		},
		{
			path: 'description',
			label: 'Description',
		},
		{
			path: 'size',
			label: 'Size (in)',
		},
		{
			path: 'nominal',
			label: 'Nominal',
		},
		{
			path: 'a',
			label: 'A',
			style: (item: MUTUTInspectionItem) => getMUTItemStyle(item),
		},
		{
			path: 'twelveOC',
			label: "12'O'c",
			style: (item: MUTUTInspectionItem) => getMUTItemStyle(item),
		},
		{
			path: 'threeOC',
			label: "3'O'c",
			style: (item: MUTUTInspectionItem) => getMUTItemStyle(item),
		},
		{
			path: 'sixOC',
			label: "6'O'c",
			style: (item: MUTUTInspectionItem) => getMUTItemStyle(item),
		},
		{
			path: 'nineOC',
			label: "9'O'c",
			style: (item: MUTUTInspectionItem) => getMUTItemStyle(item),
		},
		{
			path: 'wtLoss',
			label: '%WT.Loss',
			style: (item: MUTUTInspectionItem) => getMUTItemStyle(item),
		},
		{
			path: 'comments',
			label: 'Comments',
		},
	];
};

const getUTMColumns = (): Column[] => {
	return [
		{
			path: 'tmlLocation',
			label: 'TML',
		},
		{
			path: 'description',
			label: 'Description',
		},
		{
			path: 'size',
			label: 'Size (in)',
		},
		{
			path: 'nominal',
			label: 'Nominal',
		},
		{
			path: 'comments',
			label: 'Comments',
		},
	];
};

const getUTMPipingInspectionOptions = (): SelectOption[] => {
	return [
		{
			name: 'Pipe',
			id: 'Pipe',
		},
		{
			name: 'Elbow',
			id: 'Elbow',
		},
		{
			name: 'Reducer',
			id: 'Reducer',
		},
		{
			name: 'T',
			id: 'T',
		},
		{
			name: 'Others',
			id: 'Others',
		},
	];
};

const getUTMEquipmentInspectionOptions = (): SelectOption[] => {
	return [
		{
			name: 'Body',
			id: 'Body',
		},
		{
			name: 'Nozzel',
			id: 'Nozzel',
		},
		{
			name: 'Bottom Dish',
			id: 'Bottom Dish',
		},
		{
			name: 'Top Dish',
			id: 'Top Dish',
		},
		{
			name: 'Others',
			id: 'Others',
		},
	];
};

const getUTMStructureInspectionOptions = (): SelectOption[] => {
	return [
		{
			name: 'Others',
			id: 'Others',
		},
	];
};

const getScanCommentStyle = (item: PECScanItem): React.CSSProperties => {
	const resultOptions = getResultOptions();
	const resultOption = resultOptions.find((o) => o.id === item.comment);

	const backgroundColor: string = resultOption?.color ?? '#ffff0f';
	return { backgroundColor };
};

const getReadingStyle = (nominalThickness: number, corrosionAllowance: number, value: number, colors: string[]) => {
	const defaultStyle = {
		backgroundColor: 'transparent',
		color: '#000000'
	};

	if (Number.isNaN(nominalThickness) || Number.isNaN(corrosionAllowance)) {
		return defaultStyle;
	}

	try {
		const allowance = nominalThickness - corrosionAllowance;
		const colorScale = new ColorScale(allowance, nominalThickness, colors, 1);
		
		defaultStyle.backgroundColor = colorScale.getColor(+value).toRGBAString();

		return defaultStyle;
	} catch (error) {
		return defaultStyle;
	}
};

const getUTMReadingColumns = (startColumn: string, endColumn: string) => {
	const startIndex = ALPHABET.findIndex((letter) => letter === startColumn);
	const endIndex = ALPHABET.findIndex((letter) => letter === endColumn);

	if (startIndex !== -1 && endIndex !== -1) return ALPHABET.slice(startIndex, endIndex + 1);
	return [];
};

const getCustomUTMTableColumns = (startColumn: string, endColumn: string, includeSize: boolean, data: UTMInspection['items'], corrosionAllowance?: number) => {
	const getItemReadings = (item: UTMInspection['items'][0]) => {
		const itemKeys = Object.keys(item).map((key) => key.toUpperCase());
		const itemReadings = _.intersection(ALPHABET, itemKeys);

		const values = itemReadings.map((key) => {
			const value = Number(_.get(item, key));
			return isNaN(value) ? -1 : value;
		});

		return values.filter((value) => value >= 0);
	};

	const getItemAverage = (item: UTMInspection['items'][0]) => {
		const readings = getItemReadings(item);
		const average = _.sum(readings) / readings.length;

		return average;
	};

	const getItemMinimum = (item: UTMInspection['items'][0]) => {
		return Math.min(...getItemReadings(item));
	};

	const getItemMaximum = (item: UTMInspection['items'][0]) => {
		return Math.max(...getItemReadings(item));
	};

	const getAggregationColumns = () => {
		const columns: Column[] = [
			{
				path: 'minimum',
				label: 'Minimum',
				style: Number.isNaN(corrosionAllowance) ? undefined : (item) => ({ ...getReadingStyle(Number(item.nominal), Number(corrosionAllowance), getItemMinimum(item), getUTMColorCodes()) }),
				content: (item) => <span>{getItemMinimum(item)}</span>
			},
			{
				path: 'maximum',
				label: 'Maximum',
				style: Number.isNaN(corrosionAllowance) ? undefined : (item) => ({ ...getReadingStyle(Number(item.nominal), Number(corrosionAllowance), getItemMaximum(item), getUTMColorCodes()) }),
				content: (item) => <span>{getItemMaximum(item)}</span>
			},
			{
				path: 'average',
				label: 'Average',
				style: Number.isNaN(corrosionAllowance) ? undefined : (item) => ({ ...getReadingStyle(Number(item.nominal), Number(corrosionAllowance), getItemAverage(item), getUTMColorCodes()) }),
				content: (item) => <span>{getItemAverage(item).toFixed(2)}</span>
			},
			{
				path: 'comments',
				label: 'Comments',
			},
		];

		return columns;
	};

	const getLabelFromKey = (key: string) => {
		const labelKeyMappings = {
			size: 'Size (inches)',
			nominal: 'Nominal (mm)',
			tmllocation: 'Location Sr. No.',
			description: 'Description',
			minimum: 'Minimum',
			maximum: 'Maximum',
			average: 'Average',
			comments: 'Comments',
		};

		return _.get(labelKeyMappings, key.toLowerCase()) ?? key;
	};

	const getItemKeysFromData = () => {
		let defaultKeys = ['tmlLocation', 'description', 'nominal'];
		if (includeSize) defaultKeys.push('size');

		defaultKeys = [...defaultKeys, ...getUTMReadingColumns(startColumn, endColumn)]
		return defaultKeys;
	};

	let aggregationColumns: Column[] = getAggregationColumns();
	let defaultColumns: Column[] = getUTMColumns();

	if (data.length > 0) {
		const itemKeys = getItemKeysFromData();

		if (corrosionAllowance) {
			defaultColumns = itemKeys.map((key) => ({
				path: key,
				label: getLabelFromKey(key),
				style: (item: any) => ALPHABET.includes(key) ? ({ ...getReadingStyle(+item.nominal, +corrosionAllowance, +item[key], getUTMColorCodes()), padding: '0' }) : ({ padding: '0' }),
				content: (item: any) => {
					const value = _.get(item, key);
					if (value) return !isNaN(value) ? parseFloat(value) : value;

					return  'N/A'
				},
			}));
		} else {
			defaultColumns = itemKeys.map((key) => ({
				path: key,
				label: getLabelFromKey(key),
			}));
		}
	}

	return defaultColumns.concat(aggregationColumns);
};

const getVITableColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
		},
		{
			path: 'tagNumber',
			label: 'Identification / Tag Number',
			style: (item: VIReportItem) => getItemStyle(item),
		},
		{
			path: 'observations',
			label: 'Observation / Result',
			style: (item: VIReportItem) => getItemStyle(item),
			content: (item: VIReportItem) => (
				<ol>
					{item.observations.map((o) => (
						<li key={o.id} className="text-left">
							{o.name}
						</li>
					))}
				</ol>
			),
		},
		{
			path: 'recommendations',
			label: 'Recommendation / Action',
			style: (item: VIReportItem) => getItemStyle(item),
			content: (item: VIReportItem) => (
				<ol className="flex-couumn justify-content-center">
					{item.recommendations.map((r) => (
						<li key={r.id} className="text-left">
							{r.name}
						</li>
					))}
				</ol>
			),
		},
		{
			path: 'level',
			label: 'Criticality',
			style: (item: VIReportItem) => getItemStyle(item),
		},
		{
			path: 'images',
			label: 'Anomaly Photo Ref.',
			content: (item: VIReportItem) => (
				<span>{item.images.map((i) => '#' + i.refNo).join(', ')}</span>
			),
		},
	];
};

const getVTTableColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
			style: (item: VTAnomalyData) => getItemStyle(item),
		},
		{
			path: 'lineNumber',
			label: 'Line Number',
			style: (item: VTAnomalyData) => getItemStyle(item),
		},
		{
			path: 'pIDNumber',
			label: 'P&ID Number',
			style: (item: VTAnomalyData) => getItemStyle(item),
		},
		{
			path: 'description',
			label: 'Anomaly Description',
			style: (item: VTAnomalyData) => getItemStyle(item),
		},
		{
			path: 'recommendation',
			label: 'Recommendation',
			style: (item: VTAnomalyData) => getItemStyle(item),
		},
		{
			path: 'level',
			label: 'Criticality',
			style: (item: VTAnomalyData) => getItemStyle(item),
		},
		{
			path: 'images',
			label: 'Anomaly Photo Ref.',
			content: (item: VTAnomalyData) => (
				<span>{item.images.map((i) => '#' + i.refNo).join(', ')}</span>
			),
			style: (item: VTAnomalyData) => getItemStyle(item),
		},
	];
};

const getCustomVTTableColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'lineNumber',
			label: 'Line Number',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'pIDNumber',
			label: 'P&ID Number',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'description',
			label: 'Anomaly Description',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'recommendation',
			label: 'Recommendation',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'level',
			label: 'Criticality',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'images',
			label: 'Anomaly Photo Ref.',
			content: (item: AnomalyItem) => (
				<span>
					{[...item.closeImage, ...item.generalImage]
						.map((i) => '#' + i.refNo)
						.join(', ')}
				</span>
			),
			style: (item: AnomalyItem) => getItemStyle(item),
		},
	];
};

const getCustomFVITableColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'location',
			label: 'Location',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'description',
			label: 'Anomaly Description',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'recommendation',
			label: 'Recommendation',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'level',
			label: 'Criticality',
			style: (item: AnomalyItem) => getItemStyle(item),
		},
		{
			path: 'images',
			label: 'Anomaly Photo Ref.',
			content: (item: AnomalyItem) => (
				<span>
					{[...item.closeImage, ...item.generalImage]
						.map((i) => '#' + i.refNo)
						.join(', ')}
				</span>
			),
			style: (item: AnomalyItem) => getItemStyle(item),
		},
	];
};

const getNewCustomFVITableColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
		},
		{
			path: 'location',
			label: 'Location',
		},
		{
			path: 'description',
			label: 'Anomaly Description',
			content: (item: NewAnomalyItem) => (
				<span>{item.description.map(({ value }) => value).join('. ')}</span>
			),
		},
		{
			path: 'recommendation',
			label: 'Recommendation',
			content: (item: NewAnomalyItem) => (
				<span>{item.recommendation.map(({ value }) => value).join('. ')}</span>
			),
		},
		{
			path: 'class',
			label: 'Class',
			style: (item: NewAnomalyItem) => isNaN(item.class) ? getAnomalyTypeStyle(item) : getAnomalyClassStyle(item),
		},
		{
			path: 'images',
			label: 'Anomaly Photo Ref.',
			content: (item: NewAnomalyItem) => <span>{item.id}</span>,
		},
	];
};

const getPCNLAnomalyTableColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
		},
		{
			path: 'location',
			label: 'Location',
		},
		{
			path: 'rootCause',
			label: 'Root Cause',
		},
		{
			path: 'interventionRequired',
			label: 'Intervention Required',
		},
		{
			path: 'description',
			label: 'Anomaly Description',
			content: (item: PCNLAnomalyItem) => {
				return (
					<span>{item.description.map(({ value }) => value).join('. ')}</span>
				)
			},
		},
		{
			path: 'recommendation',
			label: 'Recommendation',
			content: (item: PCNLAnomalyItem) => (
				<span>{item.recommendation.map(({ value }) => value).join('. ')}</span>
			),
		},
		{
			path: 'riskFactor',
			label: 'Risk Factor',
			style: (item: PCNLAnomalyItem) => getPCNLAnomalyStyle(item),
		},
		{
			path: 'images',
			label: 'Anomaly Photo Ref.',
			content: (item: PCNLAnomalyItem) => <span>{item.id}</span>,
		},
	];
};

const getPCNLRiskAssementColumns = (): Column[] => {
	return [
		{
			path: 'structuralMember',
			label: 'Structural Member',
		},
		{
			path: 'hazardDescription',
			label: 'Hazard Description',
		},
		{
			path: 'chanceOfOccurrence',
			label: 'Chance of Occurrence',
			content: (item: PCNLRiskAssessmentItem) => (
				<span>{item.peopleAtRisk ? item.peopleAtRisk.map((risk) => risk.label).join('') : 'N/A'}</span>
			)
		},
		{
			path: 'hazardConsequence',
			label: 'Hazard Consequence',
		},
		{
			path: 'riskRating',
			label: 'Risk Rating',
		},
		{
			path: 'controlsRequired',
			label: 'Controls Required',
		},
		{
			path: 'controlledHazardConsequence',
			label: 'Controlled Hazard Consequence',
		},
		{
			path: 'controlledChanceOfOccurrence',
			label: 'Controlled Chance of Occurrence',
		},
		{
			path: 'residual',
			label: 'Residual',
		},
		{
			path: 'ref',
			label: 'Ref',
		},
	];
};

const getCustomVTEquipmentTableColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
			style: (item: EquipmentAnomalyItem) => getItemStyle(item),
		},
		{
			path: 'equipmentNumber',
			label: 'Equipment Tag',
			style: (item: EquipmentAnomalyItem) => getItemStyle(item),
		},
		{
			path: 'pIDNumber',
			label: 'P&ID Number',
			style: (item: EquipmentAnomalyItem) => getItemStyle(item),
		},
		{
			path: 'description',
			label: 'Anomaly Description',
			style: (item: EquipmentAnomalyItem) => getItemStyle(item),
		},
		{
			path: 'recommendation',
			label: 'Recommendation',
			style: (item: EquipmentAnomalyItem) => getItemStyle(item),
		},
		{
			path: 'level',
			label: 'Criticality',
			style: (item: EquipmentAnomalyItem) => getItemStyle(item),
		},
		{
			path: 'images',
			label: 'Anomaly Photo Ref.',
			content: (item: EquipmentAnomalyItem) => (
				<span>{item.images.map((i) => '#' + i.refNo).join(', ')}</span>
			),
			style: (item: EquipmentAnomalyItem) => getItemStyle(item),
		},
	];
};

const getVTEquipmentColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
		},
		{
			path: 'tagNumber',
			label: 'TAG Number',
			style: (item: VTEquipmentAnomalyData) => getItemStyle(item),
		},
		{
			path: 'location',
			label: 'Location',
		},
		{
			path: 'description',
			label: 'Anomaly Description',
		},
		{
			path: 'recommendation',
			label: 'Recommendation',
		},
		{
			path: 'level',
			label: 'Criticality',
			style: (item: VTEquipmentAnomalyData) => getItemStyle(item),
		},
		{
			path: 'images',
			label: 'Anomaly Photo Ref.',
			content: (item: VTEquipmentAnomalyData) => (
				<span>{item.images.map((i) => '#' + i.refNo).join(', ')}</span>
			),
		},
	];
};

const getDRTResultStyle = (result: string) => {
	if (result.toLowerCase().includes('accept')){
		return {
			backgroundColor: '#11ab45',
			color: '#ffffff',
		}
	}

	if (result.toLowerCase().includes('reject')){
		return {
			backgroundColor: '#ff0000',
			color: '#ffffff',
		}
	}

	return {
		backgroundColor: '#4472c4',
		color: '#ffffff'
	}
}

const getCustomDRTSummaryColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr. No.',
		},
		{
			path: 'weldNumber',
			label: 'WELD / JOINT NUMBER DETAILS',
		},
		{
			path: 'shotNumber',
			label: 'SHOT NUMBER',
		},
		{
			path: 'result',
			label: 'RESULT',
			style: (item) => getDRTResultStyle(item.result)
		},
		{
			path: 'defectType',
			label: 'DEFECT TYPE',
		},
	];
};

const getCustomETSummaryColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr. No.',
		},
		{
			path: 'description',
			label: 'Description',
		},
		{
			path: 'result',
			label: 'Result',
		},
		{
			path: 'images',
			label: 'Photo Number',
			content: (item: CustomLPTItem) => {
				
				return (
					<span>
						{item.images.length > 0
							? formatNumberRange(item.images.map((image) => +image.value))
							: 'N/A'}
					</span>
				)
			},
		},
	];
};

const getCustomLPTSummaryColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr. No.',
		},
		{
			path: 'joint',
			label: 'Joint / Job Number',
			style: () => ({ textAlign: 'left' })
		},
		{
			path: 'description',
			label: 'Description',
			style: () => ({ textAlign: 'left' })
		},
		{
			path: 'result',
			label: 'Result',
			style: (item) => getResultItemStyle(item),
		},
		{
			path: 'images',
			label: 'Photo Number',
			content: (item: CustomLPTItem) => {
				
				return (
					<span>
						{item.images.length > 0
							? formatNumberRange(item.images.map((image) => +image.value))
							: 'N/A'}
					</span>
				)
			},
		},
	];
};

const getCustomLPTColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr. No.',
		},
		{
			path: 'joint',
			label: 'Joint/Job Number',
			style: () => ({ textAlign: 'left' })
		},
		{
			path: 'observation',
			label: 'Observation',
			style: () => ({ textAlign: 'left' })
		},
		{
			path: 'images',
			label: 'Photo Number',
			content: (item: CustomLPTReportItem) => (
				<span>
					{item.images.length > 0
						? formatNumberRange(item.images.map((image) => +image.value))
						: 'N/A'}
				</span>
			),
		},
		{
			path: 'result',
			label: 'Results',
			style: (item) => getResultItemStyle(item),
		},
	];
};

const getLPTColumns = (): Column[] => {
	return [
		{
			path: 'srNo',
			label: 'Sr No',
		},
		{
			path: 'itemInspected',
			label: 'Item Inspected',
		},
		{
			path: 'size',
			label: 'Size (")',
		},
		{
			path: 'welderNo',
			label: 'Welder Number',
		},
		{
			path: 'observation',
			label: 'Observation',
		},
		{
			path: 'result',
			label: 'Result',
		},
	];
};

const getHelideckDTColumns = (): Column[] => {
	return [
		{
			path: 'netNumber',
			label: 'Net Number',
		},
		{
			path: 'observation',
			label: 'Observation',
		},
		{
			path: 'result',
			label: 'Result',
		},
	];
};

const getHelideckTDColumns = (): Column[] => {
	return [
		{
			path: 'netNumber',
			label: 'Net Number',
		},
		{
			path: 'testPressure',
			label: 'Test Pressure / Weight',
		},
		{
			path: 'result',
			label: 'Result',
		},
	];
};

const getHelideckVIColumns = (): Column[] => {
	return [
		{
			path: 'inspectedItem',
			label: 'Inspected Item',
		},
		{
			path: 'observation',
			label: 'Observation',
		},
		{
			path: 'remark',
			label: 'Remark',
		},
		{
			path: 'images',
			label: 'Photo Ref',
			content: (item: HelideckVIItem) => (
				<span>{item.images.map((i) => '#' + i.refNo).join(', ')}</span>
			),
		},
	];
};

const getVTStructureColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
		},
		{
			path: 'location',
			label: 'Location',
		},
		{
			path: 'pIDNumber',
			label: 'P&ID Number',
			style: (item: VTStructureAnomalyData) => getItemStyle(item),
		},
		{
			path: 'description',
			label: 'Anomaly Description',
			style: (item: VTStructureAnomalyData) => getItemStyle(item),
		},
		{
			path: 'recommendation',
			label: 'Recommendation',
		},
		{
			path: 'level',
			label: 'Criticality',
			style: (item: VTStructureAnomalyData) => getItemStyle(item),
		},
		{
			path: 'images',
			label: 'Anomaly Photo Ref.',
			content: (item: VTStructureAnomalyData) => (
				<span>{item.images.map((i) => '#' + i.refNo).join(', ')}</span>
			),
		},
	];
};

const getETAnomalyTableColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
		},
		{
			path: 'location',
			label: 'Location',
		},
		{
			path: 'description',
			label: 'Anomaly Description',
		},
		{
			path: 'recommendation',
			label: 'Recommendation',
		},
		{
			path: 'level',
			label: 'Criticality',
			style: (item: VTEquipmentAnomalyData) => getStyleFromRisk(item),
		},
		{
			path: 'images',
			label: 'Anomaly Photo Ref.',
			content: (item: VTEquipmentAnomalyData) => (
				<span>{item.images.map((i) => '#' + i.refNo).join(', ')}</span>
			),
		},
	];
};

const getHelideckFrictionTableColumns = (): Column[] => {
	return [
		{
			path: 'gridNo',
			label: 'Grid Number',
		},
		{ path: 'dimension', label: 'Dimension' },
		{ path: 'minimum', label: 'Minimum Coefficient' },
		{ path: 'maximum', label: 'Maximum Coefficient' },
		{
			path: 'average',
			label: 'Average Coefficient',
			content: (item: any) => (
				<span>{((+item.minimum + +item.maximum) / 2).toFixed(2)}</span>
			),
		},
		{ path: 'comments', label: 'Comments' },
	];
};

const getMTIColumns = (): Column[] => {
	return [
		{
			path: 'srNo',
			label: 'Sr No',
		},
		{
			path: 'itemInspected',
			label: 'Item Inspected',
		},
		{
			path: 'size',
			label: 'Size (")',
		},
		{
			path: 'welderNo',
			label: 'Welder Number',
		},
		{
			path: 'observation',
			label: 'Observation',
		},
		{
			path: 'result',
			label: 'Result',
		},
	];
};

const getWeldScanColumns = (): Column[] => {
	return [
		{
			path: 'weldNo',
			label: 'Weld Number',
		},
		{
			path: 'jointDetail',
			label: 'Joint Detail',
		},
		{
			path: 'weldDrawing',
			label: 'Weld Drawing',
			content: (item: PAUTWeldScanItem) =>
				item.weldDrawing.length > 0 ? (
					<img
						src={item.weldDrawing[0].src}
						alt={item.weldDrawing[0].caption}
					/>
				) : (
					<span>N/A</span>
				),
		},
		{
			path: 'metrics',
			label: 'Type and Length of Defect',
		},
		{
			path: 'result',
			label: 'Result',
		},
	];
};

const getPECAnomalyColumns = (): Column[] => {
	return [
		{
			path: 'scanNumber',
			label: 'Scan Number',
		},
		{
			path: 'location',
			label: 'Location',
		},
		{
			path: 'measuredThickness',
			label: 'Measured Thickness (mm)',
		},
		{
			path: 'acceptableThickness',
			label: 'Acceptable Wall Thickness (mm)',
		},
		{
			path: 'nominalThickness',
			label: 'Nominal Wall Thickness (mm)',
		},
		{
			path: 'remarks',
			label: 'Remarks',
		},
	];
};

const getPECSectionColumns = (): Column[] => {
	return [
		{
			path: 'srNo',
			label: 'Sr No.',
		},
		{
			path: 'sectionName',
			label: 'Zone Number',
		},
		{
			path: 'scanLocation',
			label: 'Scan Location',
		},
		{
			path: 'readings.data.length',
			label: 'Number of Measurements',
			content: (item) => <span>{getNumberOfMeasurementsForPECScan(item)}</span>,
		},
	];
};

const getPECScanColumns = (): Column[] => {
	return [
		{
			path: 'scanNumber',
			label: 'Scan Number',
		},
		{
			path: 'fileName',
			label: 'Zone Name',
		},
		{
			path: 'startX',
			label: 'Start X (mm)',
		},
		{
			path: 'startY',
			label: 'Start Y (mm)',
		},
		{
			path: 'length',
			label: 'Length (mm)',
		},
		{
			path: 'minimumThickness',
			label: 'Minimum Thickness',
		},
		{
			path: 'maximumThickness',
			label: 'Maximum Thickness',
		},
		{
			path: '',
			label: 'Average Thickness',
			content: (item: PECScanItem) => (
				<span>{getAverageScanThickness(item)}</span>
			),
		},
		{
			path: 'comment',
			label: 'Comment',
			style: getScanCommentStyle,
		},
	];
};

const getRTPColumns = (): Column[] => {
	return [
		{
			path: 'description',
			label: 'Anomaly Description',
			style: () => ({ textAlign: 'left' }),
		},
		{
			path: 'level',
			label: 'Corrosion Type',
			style: (item) => getStyleFromRisk(item),
		},
		{ path: 'shotNumber', label: 'Shot Number' },
	];
};

const getETWallThicknessTableColumns = (): Column[] => {
	return [
		{
			path: 'srNo',
			label: 'Location.',
		},
		{
			path: 'flangeWidth',
			label: 'Flange Width (mm)',
		},
		{
			path: 'flangeThickness',
			label: 'Flange Thickness (mm)',
		},
		{
			path: 'webLength',
			label: 'Web Length (mm)',
		},
		{
			path: 'webThickness',
			label: 'Web Thickness (mm)',
		},
		{
			path: 'diameter',
			label: 'Diameter (inches)',
		},
	];
};

const getInspectionChartColors = (): string[] => {
	return [
		'#ffa600',
		'#003f5c',
		'#2f4b7c',
		'#665191',
		'#a05195',
		'#d45087',
		'#f95d6a',
		'#ff7c43',
		'#ffa600',
	];
};

const getHelideckLineGraphData = (data: any[]) => {
	const minimum = data.map((d: any) => d.minimum);
	const maximum = data.map((d: any) => d.maximum);
	const average = data.map((d: any) => (+d.minimum + +d.maximum) / 2);

	return {
		labels: data.map((d: any) => d.gridNo),
		datasets: [
			{
				label: 'Minimum',
				data: minimum,
				borderColor: 'rgb(170, 60, 60)',
				backgroundColor: 'rgb(170, 60, 60)',
			},
			{
				label: 'Maximum',
				data: maximum,
				borderColor: 'rgb(17, 171, 69)',
				backgroundColor: 'rgb(17, 171, 69)',
			},
			{
				label: 'Average',
				data: average,
				borderColor: '#204179',
				backgroundColor: '#204179',
			},
		],
	};
};

const getUserTypes = (): SelectOption[] => {
	return [
		{
			id: USER_TYPES.INTERNAL,
			name: 'Internal User',
		},
		{
			id: USER_TYPES.EXTERNAL,
			name: 'External User',
		},
	];
};

const getRiskLevels = (): RiskLevel[] => {
	return [
		{
			level: 'minor',
			backgroundColor: 'green',
			textColor: '#fff',
		},
		{
			level: 'moderate',
			backgroundColor: '#4472c4',
			textColor: '#fff',
		},
		{
			level: 'major',
			backgroundColor: 'red',
			textColor: '#fff',
		},
	];
};

const getRiskLevelsAsOption = (): SelectOption[] => {
	return getRiskLevels().map((level: RiskLevel) => ({
		id: level.level,
		name: level.level,
	}));
};

const getPipingAnomalyClassOptions = () => {
	return [
		{
			id: DAMAGE_CLASS.ZERO.ID,
			name: DAMAGE_CLASS.ZERO.ID,
			color: DAMAGE_CLASS.ZERO.COLOR,
			descriptions: [
				{
					label: 'No Anomaly.',
					value: 'No Anomaly.',
				},
			],
			recommendations: [
				{
					label: 'No Action Required',
					value: 'No Action Required',
				},
			],
		},
		{
			id: DAMAGE_CLASS.ONE.ID,
			name: DAMAGE_CLASS.ONE.ID,
			color: DAMAGE_CLASS.ONE.COLOR,
			descriptions: [
				{
					label: 'Surface rusting only with no significant metal loss',
					value: 'Surface rusting only with no significant metal loss',
				},
				{
					label: 'Minor corrosion in support',
					value: 'Minor corrosion in support',
				},
				{
					label: 'Small bore connections in good condition',
					value: 'Small bore connections in good condition',
				},
				{
					label:
						'Coating system in good condition or showing minor breakdown < 50% but no significant underfilm corrosion',
					value:
						'Coating system in good condition or showing minor breakdown < 50% but no significant underfilm corrosion',
				},
				{
					label:
						'Coating breakdown < 50% and some minor corrosion may be occurring, but with no significant wall loss.',
					value:
						'Coating breakdown < 50% and some minor corrosion may be occurring, but with no significant wall loss.',
				},
				{
					label: 'Stains / Dents on insulation cladding.',
					value: 'Stains / Dents on insulation cladding.',
				},
				{
					label: 'Anchor bolt - bent',
					value: 'Anchor bolt - bent',
				},
				{
					label: 'Gaps / Cleareance under pipe supports',
					value: 'Gaps / Cleareance under pipe supports',
				},
				{
					label: 'Minor cracks / Air cracks on concrete supports / pedestals',
					value: 'Minor cracks / Air cracks on concrete supports / pedestals',
				},
				{
					label: 'Abrasion marks',
					value: 'Abrasion marks',
				},
				{
					label: 'Name plate not visible',
					value: 'Name plate not visible',
				},
			],
			recommendations: [
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
				{
					label: 'Continuous Monitoring Required',
					value: 'Continuous Monitoring Required',
				},
			],
		},
		{
			id: DAMAGE_CLASS.TWO.ID,
			name: DAMAGE_CLASS.TWO.ID,
			color: DAMAGE_CLASS.TWO.COLOR,
			descriptions: [
				{
					label: 'Surface rusting only with no significant metal loss',
					value: 'Surface rusting only with no significant metal loss',
				},
				{
					label: 'Minor corrosion in support with minimal metal loss',
					value: 'Minor corrosion in support with minimal metal loss',
				},
				{
					label: 'Small bore connections in good condition',
					value: 'Small bore connections in good condition',
				},
				{
					label:
						'Coating system in good condition or showing minor breakdown >= 50% but no significant underfilm corrosion',
					value:
						'Coating system in good condition or showing minor breakdown >= 50% but no significant underfilm corrosion',
				},
				{
					label:
						'Coating breakdown ≥ 50% with some corrosion, some scale build-up, and maybe minor wall loss.',
					value:
						'Coating breakdown ≥ 50% with some corrosion, some scale build-up, and maybe minor wall loss.',
				},
				{
					label:
						'Coating breakdown ≥ 50% with some corrosion and maybe minor wall loss but still with well-defined threads, flats and nuts.',
					value:
						'Coating breakdown ≥ 50% with some corrosion and maybe minor wall loss but still with well-defined threads, flats and nuts.',
				},
				{
					label: 'Some debris is likely to be found in the flange gap.',
					value: 'Some debris is likely to be found in the flange gap.',
				},
				{
					label: 'Cladding not properly sealed',
					value: 'Cladding not properly sealed',
				},
				{
					label: 'Pipe support Slided or Misalignment',
					value: 'Pipe support Slided or Misalignment',
				},
				{
					label: 'Severe cracks on concrete supports',
					value: 'Severe cracks on concrete supports',
				},
				{
					label: 'Gouges',
					value: 'Gouges',
				},
				{
					label: "A' number or Equipment ID not stamped on Name Plate",
					value: "A' number or Equipment ID not stamped on Name Plate",
				},
			],
			recommendations: [
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
			],
		},
		{
			id: DAMAGE_CLASS.THREE.ID,
			name: DAMAGE_CLASS.THREE.ID,
			color: DAMAGE_CLASS.THREE.COLOR,
			descriptions: [
				{
					label: 'Moderate rusting and minor metal loss on the external surface',
					value: 'Moderate rusting and minor metal loss on the external surface',
				},
				{
					label: 'Moderate corrosion in  support',
					value: 'Moderate corrosion in  support',
				},
				{
					label: 'Small bore connections have surface scale present',
					value: 'Small bore connections have surface scale present',
				},
				{
					label: 'Coating system breakdown occurring in some areas of pipe.',
					value: 'Coating system breakdown occurring in some areas of pipe.',
				},
				{
					label:
						'Minor coating blisters from underfilm corrosion and scale or minor scale on uncoated surface (typically 0 – 3 mm thick)',
					value:
						'Minor coating blisters from underfilm corrosion and scale or minor scale on uncoated surface (typically 0 – 3 mm thick)',
				},
				{
					label:
						'Obvious metal loss and/or clearly more than surface rust but no significant scaling and the overall shape remains.',
					value:
						'Obvious metal loss and/or clearly more than surface rust but no significant scaling and the overall shape remains.',
				},
				{
					label:
						'Likely significant build-up of rust and debris at contact point.',
					value:
						'Likely significant build-up of rust and debris at contact point.',
				},
				{
					label: 'Significant debris in the flange gap is likely.',
					value: 'Significant debris in the flange gap is likely.',
				},
				{
					label: 'Some loss of definition at the flange edges is possible.',
					value: 'Some loss of definition at the flange edges is possible.',
				},
				{
					label: 'Damaged / insecure cladding',
					value: 'Damaged / insecure cladding',
				},
				{
					label: '25% Anchor bolt missing',
					value: '25% Anchor bolt missing',
				},
				{
					label: 'Support base not securely provided',
					value: 'Support base not securely provided',
				},
				{
					label: 'Damaged concrete supports',
					value: 'Damaged concrete supports',
				},
				{
					label: 'Name plate not secure',
					value: 'Name plate not secure',
				},
				{
					label: 'Severe Abrasion',
					value: 'Severe Abrasion',
				},
			],
			recommendations: [
				{
					label: 'Repair or Replacement Required',
					value: 'Repair or Replacement Required',
				},
				// {
				// 	label: 'Blast & Coat Required',
				// 	value: 'Blast & Coat Required',
				// },
				// {
				// 	label: 'Cleaning Required',
				// 	value: 'Cleaning Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.FOUR.ID,
			name: DAMAGE_CLASS.FOUR.ID,
			color: DAMAGE_CLASS.FOUR.COLOR,
			descriptions: [
				{
					label:
						'Significant rusting and suspected significant metal loss on the external surface',
					value:
						'Significant rusting and suspected significant metal loss on the external surface',
				},
				{
					label: 'Small bore connections displaying moderate scale',
					value: 'Small bore connections displaying moderate scale',
				},
				{
					label: 'Moderate corrosion in support with heavy metal loss',
					value: 'Moderate corrosion in support with heavy metal loss',
				},
				{
					label: 'Coating system breakdown extensive',
					value: 'Coating system breakdown extensive',
				},
				{
					label:
						'Significant coating blisters from underfilm corrosion and scale or moderate scale on uncoated surface(typically 3-7mm thick)',
					value:
						'Significant coating blisters from underfilm corrosion and scale or moderate scale on uncoated surface(typically 3-7mm thick)',
				},
				{
					label:
						'Dissimilar metal contact (SS pipes directly contact with CS support).',
					value:
						'Dissimilar metal contact (SS pipes directly contact with CS support).',
				},
				{
					label: 'Insecure pipe hangings or inadequate pipe supports',
					value: 'Insecure pipe hangings or inadequate pipe supports',
				},
				{
					label: 'Significantly corroded with wall loss, and/or heavy scale.',
					value: 'Significantly corroded with wall loss, and/or heavy scale.',
				},
				{
					label:
						'Significantly corroded bolting with wall loss, heavy scale, and/or lost chunks of metal. Notable thread loss and/or necking may be observed if/where threads are visible.',
					value:
						'Significantly corroded bolting with wall loss, heavy scale, and/or lost chunks of metal. Notable thread loss and/or necking may be observed if/where threads are visible.',
				},
				{
					label:
						'Removal of nuts with a wrench may not be possible; however, there is no clear loss of connection integrity. Lack of thread engagement. Loose bolts for Non Hydrocarbon /Non SHE services.',
					value:
						'Removal of nuts with a wrench may not be possible; however, there is no clear loss of connection integrity. Lack of thread engagement. Loose bolts for Non Hydrocarbon /Non SHE services.',
				},
				{
					label:
						'The flange gap may be slightly uneven, regardless of the level of corrosion.',
					value:
						'The flange gap may be slightly uneven, regardless of the level of corrosion.',
				},
				{
					label:
						'Some loss of definition on the joint shape is possible. The flange gap may not be obvious unless viewed close up.',
					value:
						'Some loss of definition on the joint shape is possible. The flange gap may not be obvious unless viewed close up.',
				},
				{
					label:
						'Evidence of Condensation/ Wet insulations ( insluation damage-major)',
					value:
						'Evidence of Condensation/ Wet insulations ( insluation damage-major)',
				},
				{
					label: '50% Anchor bolt missing',
					value: '50% Anchor bolt missing',
				},
				{
					label: 'Internal damage',
					value: 'Internal damage',
				},
			],
			recommendations: [
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
			],
		},
		{
			id: DAMAGE_CLASS.FIVE.ID,
			name: DAMAGE_CLASS.FIVE.ID,
			color: DAMAGE_CLASS.FIVE.COLOR,
			descriptions: [
				{
					label: 'Severe rusting and suspected severe metal loss on the external surface',
					value: 'Severe rusting and suspected severe metal loss on the external surface',
				},
				{
					label: 'Small bore connections heavily scaled',
					value: 'Small bore connections heavily scaled',
				},
				{
					label: 'Major corrosion in support with damage and missing parts',
					value: 'Major corrosion in support with damage and missing parts',
				},
				{
					label:
						'Severe coating system breakdown with large ‘pocks’ of detached coating filled with very heavy corrosion scale',
					value:
						'Severe coating system breakdown with large ‘pocks’ of detached coating filled with very heavy corrosion scale',
				},
				{
					label:
						'Very heavy general scaling on uncoated surface (typically up to 7-10 mm thick)',
					value:
						'Very heavy general scaling on uncoated surface (typically up to 7-10 mm thick)',
				},
				{
					label:
						'Further scaling and corrosion in areas of previous general metal loss',
					value:
						'Further scaling and corrosion in areas of previous general metal loss',
				},
				{
					label:
						'Severely corroded including deformation, wall loss that appears to exceed the corrosion allowance, large thickening due to scale.',
					value:
						'Severely corroded including deformation, wall loss that appears to exceed the corrosion allowance, large thickening due to scale.',
				},
				{
					label:
						'Support is no longer properly constraining the pipe or even missing (if pipe in vibrating service).',
					value:
						'Support is no longer properly constraining the pipe or even missing (if pipe in vibrating service).',
				},
				{
					label:
						'Severely corroded and/or missing bolting, cracked bolting, bolts that appear to no longer be able to support the joint integrity',
					value:
						'Severely corroded and/or missing bolting, cracked bolting, bolts that appear to no longer be able to support the joint integrity',
				},
				{
					label:
						'Bolts without enough engaged threads to count.Loose bolts for Hydrocarbon / Non Hydrocarbon services.',
					value:
						'Bolts without enough engaged threads to count.Loose bolts for Hydrocarbon / Non Hydrocarbon services.',
				},
				{
					label:
						"Corners/edges of nut 'flats' rounded off, washers completely deteriorated, deformation, large thickening due to scale, flange gap may not be discernable, etc.",
					value:
						"Corners/edges of nut 'flats' rounded off, washers completely deteriorated, deformation, large thickening due to scale, flange gap may not be discernable, etc.",
				},
				{
					label:
						'Flange gap (if still discernable) is obviously uneven, regardless of the level of corrosion',
					value:
						'Flange gap (if still discernable) is obviously uneven, regardless of the level of corrosion',
				},
				{
					label: 'Missing Insulations for portion of Equipment/Piping.',
					value: 'Missing Insulations for portion of Equipment/Piping.',
				},
				{
					label: '75% Anchor bolt missing',
					value: '75% Anchor bolt missing',
				},
				{
					label: 'Foundation Damages',
					value: 'Foundation Damages',
				},
				{
					label: 'Name plate missing',
					value: 'Name plate missing',
				},
				{
					label: 'PSV Service Overdue',
					value: 'PSV Service Overdue',
				},
			],
			recommendations: [
				{
					label: 'Repair or Replacement Required',
					value: 'Repair or Replacement Required',
				},
				// {
				// 	label: 'Blast & Coat Required',
				// 	value: 'Blast & Coat Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.SIX.ID,
			name: DAMAGE_CLASS.SIX.ID,
			color: DAMAGE_CLASS.SIX.COLOR,
			descriptions: [
				{
					label: 'Leak or structural failure suspected or imminent',
					value: 'Leak or structural failure suspected or imminent',
				},
				{
					label: 'Leaking, Rust scale is black and oily, ‘weeping’',
					value: 'Leaking, Rust scale is black and oily, ‘weeping’',
				},
				{
					label: 'Corrosion scale is thicker than 10mm',
					value: 'Corrosion scale is thicker than 10mm',
				},
				{
					label: 'Multiple corrosion leaks have already occurred due to atmospheric corrosion in the last 5 years',
					value: 'Multiple corrosion leaks have already occurred due to atmospheric corrosion in the last 5 years',
				},
				{
					label:
						'Debris at contact point may be moist but other nearby material is not or corrosion scale is thicker than 10 mm.',
					value:
						'Debris at contact point may be moist but other nearby material is not or corrosion scale is thicker than 10 mm.',
				},
				{
					label:
						'Anything that appears to be an immediate issue including bolts that appear to no longer be able to support the joint integrity, bolts without enough engaged threads to count, missing bolts/nuts.',
					value:
						'Anything that appears to be an immediate issue including bolts that appear to no longer be able to support the joint integrity, bolts without enough engaged threads to count, missing bolts/nuts.',
				},
				{
					label:
						'Anything that appears to be an immediate issue including deformation, large thickening due to scale, flange gap is not discernable, etc.',
					value:
						'Anything that appears to be an immediate issue including deformation, large thickening due to scale, flange gap is not discernable, etc.',
				},
				{
					label: 'Service leak from Insulation.',
					value: 'Service leak from Insulation.',
				},
				{
					label: '100% Anchor bolt missing',
					value: '100% Anchor bolt missing',
				},
				{
					label: 'Support is missing',
					value: 'Support is missing',
				},
			],
			recommendations: [
				{
					label: 'Immediate Action Required',
					value: 'Immediate Action Required',
				},
			],
		},
	];
};

const getEquipmentAnomalyClassOptions = () => {
	return [
		{
			id: DAMAGE_CLASS.ZERO.ID,
			name: DAMAGE_CLASS.ZERO.ID,
			color: DAMAGE_CLASS.ZERO.COLOR,
			descriptions: [
				{
					label: 'No Anomaly.',
					value: 'No Anomaly.',
				},
			],
			recommendations: [
				{
					label: 'No Action Required',
					value: 'No Action Required',
				},
			],
		},
		{
			id: DAMAGE_CLASS.ONE.ID,
			name: DAMAGE_CLASS.ONE.ID,
			color: DAMAGE_CLASS.ONE.COLOR,
			descriptions: [
				{
					label: 'Surface rusting only with no significant metal loss',
					value: 'Surface rusting only with no significant metal loss',
				},
				{
					label: 'Minor corrosion in support',
					value: 'Minor corrosion in support',
				},
				{
					label: 'Flanges and Fasteners are in good condition',
					value: 'Flanges and Fasteners are in good condition',
				},
				{
					label: 'Fasteners are in good condition',
					value: 'Fasteners are in good condition',
				},
				{
					label:
						'Coating breakdown < 50% and some minor corrosion may be occurring, but with no significant wall loss.',
					value:
						'Coating breakdown < 50% and some minor corrosion may be occurring, but with no significant wall loss.',
				},
				{
					label:
						'Coating system in good condition or showing minor  breakdown < 50% but no significant underfilm corrosion',
					value:
						'Coating system in good condition or showing minor  breakdown < 50% but no significant underfilm corrosion',
				},
				{
					label: 'Stains / Dents on insulation cladding.',
					value: 'Stains / Dents on insulation cladding.',
				},
				{
					label: 'Anchor bolt - bent',
					value: 'Anchor bolt - bent',
				},
				{
					label: 'Gaps / Cleareance under supports',
					value: 'Gaps / Cleareance under supports',
				},
				{
					label: 'Minor cracks / Air cracks on concrete supports / pedestals',
					value: 'Minor cracks / Air cracks on concrete supports / pedestals',
				},
				{
					label: 'Abrasion marks',
					value: 'Abrasion marks',
				},
				{
					label: 'Name plate not visible',
					value: 'Name plate not visible',
				},
				{
					label: 'Name plate missing',
					value: 'Name plate missing',
				},
			],
			recommendations: [
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
				// {
				// 	label: 'No Immediate Action Required',
				// 	value: 'No Immediate Action Required',
				// },
				// {
				// 	label: 'Cleaning Required',
				// 	value: 'Cleaning Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.TWO.ID,
			name: DAMAGE_CLASS.TWO.ID,
			color: DAMAGE_CLASS.TWO.COLOR,
			descriptions: [
				{
					label: 'Surface rusting only with no significant metal loss',
					value: 'Surface rusting only with no significant metal loss',
				},
				{
					label: 'Minor corrosion in support with minimal metal loss',
					value: 'Minor corrosion in support with minimal metal loss',
				},
				{
					label: 'Flanges and Fasteners - Minor Corrosion',
					value: 'Flanges and Fasteners - Minor Corrosion',
				},
				{
					label: 'Fasteners - Minor Corrosion',
					value: 'Fasteners - Minor Corrosion',
				},
				{
					label:
						'Coating system in good condition or showing minor breakdown ≥ 50% but no significant underfilm corrosion',
					value:
						'Coating system in good condition or showing minor breakdown ≥ 50% but no significant underfilm corrosion',
				},
				{
					label:
						'Coating breakdown ≥ 50% with some corrosion, some scale build-up, and maybe minor wall loss.',
					value:
						'Coating breakdown ≥ 50% with some corrosion, some scale build-up, and maybe minor wall loss.',
				},
				{
					label: 'Debris found',
					value: 'Debris found',
				},
				{
					label: 'Cladding not properly sealed',
					value: 'Cladding not properly sealed',
				},
				{
					label: 'Support Slided or Misalignment',
					value: 'Support Slided or Misalignment',
				},
				{
					label: 'Severe cracks on concrete supports',
					value: 'Severe cracks on concrete supports',
				},
				{
					label: 'Gouges',
					value: 'Gouges',
				},
				{
					label: 'Number or Equipment ID not stamped on Name Plate',
					value: 'Number or Equipment ID not stamped on Name Plate',
				},
			],
			recommendations: [
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
				// {
				// 	label: 'No Immediate Action Required',
				// 	value: 'No Immediate Action Required',
				// },
				// {
				// 	label: 'Cleaning Required',
				// 	value: 'Cleaning Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.THREE.ID,
			name: DAMAGE_CLASS.THREE.ID,
			color: DAMAGE_CLASS.THREE.COLOR,
			descriptions: [
				{
					label: 'Moderate rusting and minor metal loss on the external surface',
					value: 'Moderate rusting and minor metal loss on the external surface',
				},
				{
					label: 'Flanges and Fasteners - Scale buildup',
					value: 'Flanges and Fasteners - Scale buildup',
				},
				{
					label: 'Moderate corrosion in support',
					value: 'Moderate corrosion in support',
				},
				{
					label:
						'Some loss of threads may be  occurring but they remain reasonably  well-defined and there is no significant  necking. Removal of nuts with a wrench  might still be possible (but is not certain).',
					value:
						'Some loss of threads may be  occurring but they remain reasonably  well-defined and there is no significant  necking. Removal of nuts with a wrench  might still be possible (but is not certain).',
				},
				{
					label:
						'Minor coating blisters from underfilm corrosion and scale or minor scale on uncoated surface (typically 0 – 3 mm thick)',
					value:
						'Minor coating blisters from underfilm corrosion and scale or minor scale on uncoated surface (typically 0 – 3 mm thick)',
				},
				{
					label:
						'Obvious metal loss and/or clearly more than surface rust but no significant scaling and the overall shape remains.',
					value:
						'Obvious metal loss and/or clearly more than surface rust but no significant scaling and the overall shape remains.',
				},
				{
					label:
						'Likely significant build-up of rust and debris at contact point.',
					value:
						'Likely significant build-up of rust and debris at contact point.',
				},
				{
					label: 'Some loss of definition at the flange edges is possible',
					value: 'Some loss of definition at the flange edges is possible',
				},
				{
					label: 'Damaged / insecure cladding.',
					value: 'Damaged / insecure cladding.',
				},
				{
					label: 'Significant debris',
					value: 'Significant debris',
				},
				{
					label: '25% Anchor bolt missing',
					value: '25% Anchor bolt missing',
				},
				{
					label: 'Support base not securely provided',
					value: 'Support base not securely provided',
				},
				{
					label: 'Damaged concrete supports',
					value: 'Damaged concrete supports',
				},
				{
					label: 'Name plate not secure',
					value: 'Name plate not secure',
				},
				{
					label: 'Severe Abrasion',
					value: 'Severe Abrasion',
				},
			],
			recommendations: [
				{
					label: 'Repair or Replacement Required',
					value: 'Repair or Replacement Required',
				},
				// {
				// 	label: 'No Immediate Action Required',
				// 	value: 'No Immediate Action Required',
				// },
				// {
				// 	label: 'Cleaning Required',
				// 	value: 'Cleaning Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.FOUR.ID,
			name: DAMAGE_CLASS.FOUR.ID,
			color: DAMAGE_CLASS.FOUR.COLOR,
			descriptions: [
				{
					label: 'Significant rusting and suspected significant metal loss on the external surface',
					value: 'Significant rusting and suspected significant metal loss on the external surface',
				},
				{
					label: 'Flanges and Fasteners -Heavy scale buildup',
					value: 'Flanges and Fasteners -Heavy scale buildup',
				},
				{
					label: 'Moderate corrosion in support with heavy metal loss',
					value: 'Moderate corrosion in support with heavy metal loss',
				},
				{
					label: 'Coating system breakdown extensive',
					value: 'Coating system breakdown extensive',
				},
				{
					label:
						'Significant coating blisters from underfilm corrosion and scale or moderate scale on uncoated surface(typically 3-7mm thick)',
					value:
						'Significant coating blisters from underfilm corrosion and scale or moderate scale on uncoated surface(typically 3-7mm thick)',
				},
				{
					label: 'Significantly corroded with wall loss, and/or heavy scale.',
					value: 'Significantly corroded with wall loss, and/or heavy scale.',
				},
				{
					label:
						'Significantly corroded bolting with wall loss, heavy scale, and/or lost chunks of metal. Notable thread loss and/or necking may be observed if/where threads are visible.',
					value:
						'Significantly corroded bolting with wall loss, heavy scale, and/or lost chunks of metal. Notable thread loss and/or necking may be observed if/where threads are visible.',
				},
				{
					label:
						'Removal of nuts with a wrench may not be possible; however, there is no clear loss of connection integrity. Lack of thread engagement. Loose bolts for Non Hydrocarbon /Non SHE services.',
					value:
						'Removal of nuts with a wrench may not be possible; however, there is no clear loss of connection integrity. Lack of thread engagement. Loose bolts for Non Hydrocarbon /Non SHE services.',
				},
				{
					label:
						'The flange gap may be slightly uneven, regardless of the level of corrosion.',
					value:
						'The flange gap may be slightly uneven, regardless of the level of corrosion.',
				},
				{
					label:
						'Some loss of definition on the joint shape is possible. The flange gap may not be obvious unless viewed close up.',
					value:
						'Some loss of definition on the joint shape is possible. The flange gap may not be obvious unless viewed close up.',
				},
				{
					label:
						'Evidence of Condensation/ Wet insulations ( insluation damage-major)',
					value:
						'Evidence of Condensation/ Wet insulations ( insluation damage-major)',
				},
				{
					label: '75% Anchor bolt missing',
					value: '75% Anchor bolt missing',
				},
				{
					label: 'Internal damage',
					value: 'Internal damage',
				},
			],
			recommendations: [
				{
					label: 'Repair or Replacement Required',
					value: 'Repair or Replacement Required',
				},
				// {
				// 	label: 'Blast & Coat Required',
				// 	value: 'Blast & Coat Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.FIVE.ID,
			name: DAMAGE_CLASS.FIVE.ID,
			color: DAMAGE_CLASS.FIVE.COLOR,
			descriptions: [
				{
					label: 'Severe rusting and suspected severe metal loss on the external surface',
					value: 'Severe rusting and suspected severe metal loss on the external surface',
				},
				{
					label: 'Fasteners and Flanges - Major corrosion',
					value: 'Fasteners and Flanges - Major corrosion',
				},
				{
					label: 'Major corrosion in support with damage and missing parts',
					value: 'Major corrosion in support with damage and missing parts',
				},
				{
					label: 'Fasteners - Major corrosion',
					value: 'Fasteners - Major corrosion',
				},
				{
					label:
						'Very heavy general scaling on uncoated surface (typically up to 7-10 mm thick)',
					value:
						'Very heavy general scaling on uncoated surface (typically up to 7-10 mm thick)',
				},
				{
					label: 'General metal loss',
					value: 'General metal loss',
				},
				{
					label:
						'Severely corroded including deformation, wall loss that appears to exceed the corrosion allowance, large thickening due to scale.',
					value:
						'Severely corroded including deformation, wall loss that appears to exceed the corrosion allowance, large thickening due to scale.',
				},
				{
					label:
						'Severely corroded and/or missing bolting, cracked bolting, bolts that appear to no longer be able to support the joint integrity',
					value:
						'Severely corroded and/or missing bolting, cracked bolting, bolts that appear to no longer be able to support the joint integrity',
				},
				{
					label:
						'Bolts without enough engaged threads to count.Loose bolts for Hydrocarbon / Non Hydrocarbon services.',
					value:
						'Bolts without enough engaged threads to count.Loose bolts for Hydrocarbon / Non Hydrocarbon services.',
				},
				{
					label:
						"Corners/edges of nut 'flats' rounded off, washers completely deteriorated, deformation, large thickening due to scale, flange gap may not be discernable, etc.",
					value:
						"Corners/edges of nut 'flats' rounded off, washers completely deteriorated, deformation, large thickening due to scale, flange gap may not be discernable, etc.",
				},
				{
					label:
						'Further scaling and corrosion in areas of previous general metal loss',
					value:
						'Further scaling and corrosion in areas of previous general metal loss',
				},
				{
					label: 'Missing Insulations.',
					value: 'Missing Insulations.',
				},
				{
					label: '100% Anchor bolt missing',
					value: '100% Anchor bolt missing',
				},
				{
					label: 'Foundation Damages',
					value: 'Foundation Damages',
				},
				{
					label: 'Name plate missing',
					value: 'Name plate missing',
				},
				{
					label: 'PSV Service Overdue',
					value: 'PSV Service Overdue',
				},
			],
			recommendations: [
				{
					label: 'Repair or Replacement Required',
					value: 'Repair or Replacement Required',
				},
				// {
				// 	label: 'Blast & Coat Required',
				// 	value: 'Blast & Coat Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.SIX.ID,
			name: DAMAGE_CLASS.SIX.ID,
			color: DAMAGE_CLASS.SIX.COLOR,
			descriptions: [
				{
					label: 'Leak',
					value: 'Leak',
				},
				{
					label: 'Missing Flanges',
					value: 'Missing Flanges',
				},
				{
					label: 'Missing Fasteners',
					value: 'Missing Fasteners',
				},
				{
					label: 'Missing Parts',
					value: 'Missing Parts',
				},
				{
					label: 'Not in working condition',
					value: 'Not in working condition',
				},
				{
					label: 'Support is missing',
					value: 'Support is missing',
				},
			],
			recommendations: [
				{
					label: 'Immediate Action Required',
					value: 'Immediate Action Required',
				},
			],
		},
	];
};

const getAnomalyTypeClassOptions = () => {
	return [
		{
			id: ANOMALY_TYPE.NONE.ID,
			name: ANOMALY_TYPE.NONE.ID,
			color: ANOMALY_TYPE.NONE.COLOR,
			descriptions: [
				{
					label: 'System generally intact with no signs of corrosion.',
					value: 'System generally intact with no signs of corrosion.',
				},
				{
					label: 'Connection bolts / nuts in good condition with no corrosion',
					value: 'Connection bolts / nuts in good condition with no corrosion',
				},
				{
					label: 'Previous repairs in good condition and well protected',
					value: 'Previous repairs in good condition and well protected',
				},
				{
					label: 'No evidence of misalignment or distortion of members',
					value: 'No evidence of misalignment or distortion of members',
				},
				{
					label: 'Fireproofing in good condition with no cracks or rust stains',
					value: 'Fireproofing in good condition with no cracks or rust stains',
				},
			],
			recommendations: [
				{
					label: 'No Action Required',
					value: 'No Action Required',
				},
			],
		},
		{
			id: ANOMALY_TYPE.MINOR.ID,
			name: ANOMALY_TYPE.MINOR.ID,
			color: ANOMALY_TYPE.MINOR.COLOR,
			descriptions: [
				{
					label: 'Coating system failure over more than 10% of area',
					value: 'Coating system failure over more than 10% of area',
				},
				{
					label: 'Heavy rust scale',
					value: 'Heavy rust scale',
				},
				{
					label: 'Deep pits',
					value: 'Deep pits',
				},
				{
					label: 'Less than 10% corrosion loss of cross section',
					value: 'Less than 10% corrosion loss of cross section',
				},
				{
					label: 'Connection bolts / nuts and anchor bolts / nuts corroded but still defined threads and flats',
					value: 'Connection bolts / nuts and anchor bolts / nuts corroded but still defined threads and flats',
				},
				{
					label: 'Impact damage resulting in member being bent or distorted up to half the depth of the member',
					value: 'Impact damage resulting in member being bent or distorted up to half the depth of the member',
				},
				{
					label: 'No evidence of other misalignment or distortion of members',
					value: 'No evidence of other misalignment or distortion of members',
				},
				{
					label: 'Fireproofing cracks <0.25” (<6mm) wide',
					value: 'Fireproofing cracks <0.25” (<6mm) wide',
				},
			],
			recommendations: [
				{
					label: 'No Immediate Action Required',
					value: 'No Immediate Action Required',
				},
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
				{
					label: 'Cleaning Required',
					value: 'Cleaning Required',
				},
				{
					label: 'Continuous Monitoring Required',
					value: 'Continuous Monitoring Required',
				},
			],
		},
		{
			id: ANOMALY_TYPE.MINOR_TO_MODERATE.ID,
			name: ANOMALY_TYPE.MINOR_TO_MODERATE.ID,
			color: ANOMALY_TYPE.MINOR_TO_MODERATE.COLOR,
			descriptions: [
				{
					label: 'Corrosion loss of cross section between 10% and 25%',
					value: 'Corrosion loss of cross section between 10% and 25%',
				},
				{
					label: 'Holing of member',
					value: 'Holing of member',
				},
				{
					label: 'Extensive cracking of fireproofing with cracks >0.25” (>6mm) wide',
					value: 'Extensive cracking of fireproofing with cracks >0.25” (>6mm) wide',
				},
				{
					label: 'Impact damage resulting in member being distorted or bent greater than half the depth of the member',
					value: 'Impact damage resulting in member being distorted or bent greater than half the depth of the member',
				},
				{
					label: 'Noticeable deflections or misalignment of members',
					value: 'Noticeable deflections or misalignment of members',
				},
				{
					label: 'Part / Item  leaning from vertical',
					value: 'Part / Item  leaning from vertical',
				},
				{
					label: 'Connection deficient, such as one missing bolt or nut',
					value: 'Connection deficient, such as one missing bolt or nut',
				},
				{
					label: 'Severely corroded connection bolts',
					value: 'Severely corroded connection bolts',
				},
			],
			recommendations: [
				{
					label: 'No Immediate Action Required',
					value: 'No Immediate Action Required',
				},
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
				{
					label: 'Cleaning Required',
					value: 'Cleaning Required',
				},
			],
		},
		{
			id: ANOMALY_TYPE.MODERATE.ID,
			name: ANOMALY_TYPE.MODERATE.ID,
			color: ANOMALY_TYPE.MODERATE.COLOR,
			descriptions: [
				{
					label: 'Similar to Condition class B, but gives cause for concern',
					value: 'Similar to Condition class B, but gives cause for concern',
				},
				{
					label: 'Corrosion loss of cross section between 35% and 50%',
					value: 'Corrosion loss of cross section between 35% and 50%',
				},
				{
					label: 'Two or more of the bolts or nuts broken or missing in a connection',
					value: 'Two or more of the bolts or nuts broken or missing in a connection',
				},
				{
					label: 'Gross distortion such as bent out of line greater than 1 times the depth of member',
					value: 'Gross distortion such as bent out of line greater than 1 times the depth of member',
				},
				{
					label: 'Gross deflections or misalignment of members',
					value: 'Gross deflections or misalignment of members',
				},
				{
					label: 'Member missing',
					value: 'Member missing',
				},
				{
					label: 'The member is judged to be potentially dangerous requiring early action',
					value: 'The member is judged to be potentially dangerous requiring early action',
				},
			],
			recommendations: [
				{
					label: 'No Immediate Action Required',
					value: 'No Immediate Action Required',
				},
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
				{
					label: 'Cleaning Required',
					value: 'Cleaning Required',
				},
			],
		},
		{
			id: ANOMALY_TYPE.MAJOR.ID,
			name: ANOMALY_TYPE.MAJOR.ID,
			color: ANOMALY_TYPE.MAJOR.COLOR,
			descriptions: [
				{
					label: 'Similar to Condition class C, but gives cause for serious concern',
					value: 'Similar to Condition class C, but gives cause for serious concern',
				},
				{
					label: 'Corrosion loss of cross section greater than 50% of the gross section',
					value: 'Corrosion loss of cross section greater than 50% of the gross section',
				},
				{
					label: 'Two or more of the bolts or nuts broken or missing in a connection',
					value: 'Two or more of the bolts or nuts broken or missing in a connection',
				},
				{
					label: 'Gross distortion such as bent out of line greater than 2 times the depth of member',
					value: 'Gross distortion such as bent out of line greater than 2 times the depth of member',
				},
				{
					label: 'Member missing',
					value: 'Member missing',
				},
				{
					label: 'The member is judged to be potentially dangerous requiring corrective action',
					value: 'The member is judged to be potentially dangerous requiring corrective action',
				},
				{
					label: 'Functional Failure',
					value: 'Functional Failure',
				},
			],
			recommendations: [
				{
					label: 'Repair or Replacement Required',
					value: 'Repair or Replacement Required',
				},
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
			],
		},
	]
}

const getStructureAnomalyClassOptions = () => {
	return [
		{
			id: DAMAGE_CLASS.ZERO.ID,
			name: DAMAGE_CLASS.ZERO.ID,
			color: DAMAGE_CLASS.ZERO.COLOR,
			descriptions: [
				{
					label: 'No Anomaly.',
					value: 'No Anomaly.',
				},
			],
			recommendations: [
				{
					label: 'No Action Required',
					value: 'No Action Required',
				},
			],
		},
		{
			id: DAMAGE_CLASS.ONE.ID,
			name: DAMAGE_CLASS.ONE.ID,
			color: DAMAGE_CLASS.ONE.COLOR,
			descriptions: [
				{
					label: 'Oil stain found',
					value: 'Oil stain found',
				},
				{
					label: 'Surface rusting only with no significant metal loss',
					value: 'Surface rusting only with no significant metal loss',
				},
				{
					label: 'Minor corrosion in support',
					value: 'Minor corrosion in support',
				},
				{
					label: 'Fasteners are in good condition',
					value: 'Fasteners are in good condition',
				},
				{
					label: 'Coating breakdown with minor corrosion',
					value: 'Coating breakdown with minor corrosion',
				},
				{
					label:
						'Coating system in good condition or showing minor  breakdown < 50% but no significant underfilm corrosion',
					value:
						'Coating system in good condition or showing minor  breakdown < 50% but no significant underfilm corrosion',
				},
				{
					label: 'No coating noticed',
					value: 'No coating noticed',
				},
				{
					label: 'Debris found',
					value: 'Debris found',
				},
				{
					label: 'Anchor bolt - bent or damage',
					value: 'Anchor bolt - bent or damage',
				},
				{
					label: 'Minor cracks / Air cracks on concrete supports / pedestals',
					value: 'Minor cracks / Air cracks on concrete supports / pedestals',
				},
				{
					label: 'Gaps / Cleareance under supports',
					value: 'Gaps / Cleareance under supports',
				},
				{
					label: 'Sign board  not visible',
					value: 'Sign board  not visible',
				},
			],
			recommendations: [
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
				// {
				// 	label: 'No Immediate Action Required',
				// 	value: 'No Immediate Action Required',
				// },
				// {
				// 	label: 'Cleaning Required',
				// 	value: 'Cleaning Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.TWO.ID,
			name: DAMAGE_CLASS.TWO.ID,
			color: DAMAGE_CLASS.TWO.COLOR,
			descriptions: [
				{
					label: 'Coating damage',
					value: 'Coating damage',
				},
				{
					label: 'Surface rusting only with no significant metal loss',
					value: 'Surface rusting only with no significant metal loss',
				},
				{
					label: 'Minor corrosion in support with minimal metal loss',
					value: 'Minor corrosion in support with minimal metal loss',
				},
				{
					label: 'Fasteners - Minor Corrosion',
					value: 'Fasteners - Minor Corrosion',
				},
				{
					label:
						'Coating system in good condition or showing minor breakdown ≥ 50% but no significant underfilm corrosion',
					value:
						'Coating system in good condition or showing minor breakdown ≥ 50% but no significant underfilm corrosion',
				},
				{
					label: 'Minor corrosion with metal loss',
					value: 'Minor corrosion with metal loss',
				},
				{
					label: 'Debris found',
					value: 'Debris found',
				},
				{
					label: 'Cladding not properly sealed',
					value: 'Cladding not properly sealed',
				},
				{
					label: 'Support Slided or Misalignment',
					value: 'Support Slided or Misalignment',
				},
				{
					label: 'Some Anchor bolt missing',
					value: 'Some Anchor bolt missing',
				},
				{
					label: 'Severe cracks on concrete supports',
					value: 'Severe cracks on concrete supports',
				},
				{
					label: 'Sign board missing',
					value: 'Sign board missing',
				},
			],
			recommendations: [
				{
					label: 'Blast & Coat Required',
					value: 'Blast & Coat Required',
				},
				// {
				// 	label: 'No Immediate Action Required',
				// 	value: 'No Immediate Action Required',
				// },
				// {
				// 	label: 'Cleaning Required',
				// 	value: 'Cleaning Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.THREE.ID,
			name: DAMAGE_CLASS.THREE.ID,
			color: DAMAGE_CLASS.THREE.COLOR,
			descriptions: [
				{
					label: 'Pitting',
					value: 'Pitting',
				},
				{
					label: 'Moderate rusting and minor metal loss on the external surface',
					value: 'Moderate rusting and minor metal loss on the external surface',
				},
				{
					label: 'Moderate corrosion in  support',
					value: 'Moderate corrosion in  support',
				},
				{
					label: 'Fasteners - Scale buildup',
					value: 'Fasteners - Scale buildup',
				},
				{
					label: 'Coating system breakdown occurring in some areas.',
					value: 'Coating system breakdown occurring in some areas.',
				},
				{
					label:
						'Minor coating blisters from underfilm corrosion and scale or minor scale on uncoated surface (typically 0 – 3 mm thick)',
					value:
						'Minor coating blisters from underfilm corrosion and scale or minor scale on uncoated surface (typically 0 – 3 mm thick)',
				},
				{
					label: 'Moderate corrosion.',
					value: 'Moderate corrosion.',
				},
				{
					label:
						'Moderate rusting and minor metal loss on the external surface',
					value:
						'Moderate rusting and minor metal loss on the external surface',
				},
				{
					label: 'Significant debris',
					value: 'Significant debris',
				},
				{
					label: 'Damaged / insecure cladding.',
					value: 'Damaged / insecure cladding.',
				},
				{
					label: '25% Anchor bolt missing',
					value: '25% Anchor bolt missing',
				},
				{
					label: 'Support base not securely provided',
					value: 'Support base not securely provided',
				},
				{
					label: 'Damaged concrete supports',
					value: 'Damaged concrete supports',
				},
			],
			recommendations: [
				{
					label: 'Repair or Replacement Required',
					value: 'Repair or Replacement Required',
				},
				// {
				// 	label: 'Blast & Coat Required',
				// 	value: 'Blast & Coat Required',
				// },
				// {
				// 	label: 'Cleaning Required',
				// 	value: 'Cleaning Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.FOUR.ID,
			name: DAMAGE_CLASS.FOUR.ID,
			color: DAMAGE_CLASS.FOUR.COLOR,
			descriptions: [
				{
					label: 'Significant rusting and suspected significant metal loss on the external surface',
					value: 'Significant rusting and suspected significant metal loss on the external surface',
				},
				{
					label: 'Fasteners -Heavy scale buildup',
					value: 'Fasteners -Heavy scale buildup',
				},
				{
					label: 'Moderate corrosion  in support with heavy metal loss',
					value: 'Moderate corrosion  in support with heavy metal loss',
				},
				{
					label: 'Coating system breakdown extensive',
					value: 'Coating system breakdown extensive',
				},
				{
					label:
						'Significant coating blisters from underfilm corrosion and scale or moderate scale on uncoated surface(typically 3-7mm thick)',
					value:
						'Significant coating blisters from underfilm corrosion and scale or moderate scale on uncoated surface(typically 3-7mm thick)',
				},
				{
					label: 'Major corrosion with metal loss',
					value: 'Major corrosion with metal loss',
				},
				{
					label: '75% Anchor bolt missing',
					value: '75% Anchor bolt missing',
				},
				{
					label: 'Internal damage',
					value: 'Internal damage',
				},
			],
			recommendations: [
				{
					label: 'Repair or Replacement Required',
					value: 'Repair or Replacement Required',
				},
				// {
				// 	label: 'Blast & Coat Required',
				// 	value: 'Blast & Coat Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.FIVE.ID,
			name: DAMAGE_CLASS.FIVE.ID,
			color: DAMAGE_CLASS.FIVE.COLOR,
			descriptions: [
				{
					label: 'Major Pitting.',
					value: 'Major Pitting.',
				},
				{
					label: 'Severe rusting and suspected severe metal loss on the external surface',
					value: 'Severe rusting and suspected severe metal loss on the external surface',
				},
				{
					label: 'Major corrosion in support with damage and missing parts',
					value: 'Major corrosion in support with damage and missing parts',
				},
				{
					label: 'Fasteners - Major corrosion',
					value: 'Fasteners - Major corrosion',
				},
				{
					label:
						'Severe coating system breakdown with large ‘pocks’ of  detached coating filled with very heavy corrosion scale',
					value:
						'Severe coating system breakdown with large ‘pocks’ of  detached coating filled with very heavy corrosion scale',
				},
				{
					label:
						'Very heavy general scaling on uncoated surface (typically up to 7-10 mm thick)',
					value:
						'Very heavy general scaling on uncoated surface (typically up to 7-10 mm thick)',
				},
				{
					label: 'Damaged structural part',
					value: 'Damaged structural part',
				},
				{
					label:
						'Further scaling and corrosion in areas of previous general metal loss',
					value:
						'Further scaling and corrosion in areas of previous general metal loss',
				},
				{
					label: '100% Anchor bolt missing',
					value: '100% Anchor bolt missing',
				},
				{
					label: 'Foundation Damages',
					value: 'Foundation Damages',
				},
			],
			recommendations: [
				{
					label: 'Repair or Replacement Required',
					value: 'Repair or Replacement Required',
				},
				// {
				// 	label: 'Blast & Coat Required',
				// 	value: 'Blast & Coat Required',
				// },
			],
		},
		{
			id: DAMAGE_CLASS.SIX.ID,
			name: DAMAGE_CLASS.SIX.ID,
			color: DAMAGE_CLASS.SIX.COLOR,
			descriptions: [
				{
					label: 'Failure suspected.',
					value: 'Failure suspected.',
				},
				{
					label: 'Hole found.',
					value: 'Hole found.',
				},
				{
					label: 'Support is missing',
					value: 'Support is missing',
				},
				{
					label: 'Structural part is missing.',
					value: 'Structural part is missing.',
				},
				{
					label: 'Handrail damaged.',
					value: 'Handrail damaged.',
				},
			],
			recommendations: [
				{
					label: 'Immediate Action Required',
					value: 'Immediate Action Required',
				},
			],
		},
	];
};

const getRiskLevelColors = (): string[] => {
	return getRiskLevels().map((l) => l.backgroundColor);
};

const getReviewOptions = (): SelectOption[] => [
	{
		id: 'approved',
		name: 'I approve',
	},
	{
		id: 'rejected',
		name: 'I do not approve',
	},
];

const getSectionOptions = (): SelectOption[] => [
	{
		id: 'internal',
		name: 'Internal',
	},
	{
		id: 'external',
		name: 'External',
	},
];

const getAcceptanceOptions = (): SelectOption[] => [
	{
		id: 'accepted',
		name: 'I accept',
	},
	{
		id: 'rejected',
		name: 'I do not accept',
	},
];

const getResultOptions = (): InspectionResultItem[] => {
	return [
		{ id: 'Accepted', name: 'Accepted', color: '#11ab45' },
		{ id: 'Rejected', name: 'Rejected', color: '#ff0000' },
		{
			id: 'Partially Accepted',
			name: 'Partially Accepted',
			color: '#92d051',
		},
		{
			id: 'Partially Rejected',
			name: 'Partially Rejected',
			color: 'orange',
		},
		{
			id: 'Not done due to surface concern',
			name: 'Not done due to surface concern',
			color: '#4472c4',
		},
		{
			id: 'Restricted Area',
			name: 'Restricted Area',
			color: '#2a343f',
		},
	];
};

const getResultOptionsForDRTReport = (): InspectionResultItem[] => {
	return [
		{ id: 'Accepted', name: 'Accepted', color: '#11ab45' },
		{ id: 'Rejected', name: 'Rejected', color: '#ff0000' },
		{ id: 'Restrictions', name: 'Restrictions', color: '#0782c8' },
		{ id: 'Partially Covered', name: 'Partially Covered', color: '#ffff0f' },
		{ id: 'No Access', name: 'No Access', color: '#5d9bd5' },
		{
			id: 'Other NDT Method Recommended',
			name: 'Other NDT Method Recommended',
			color: '#5d9bd5'
		},
	];
};

const getHelideckResultOptions = (): SelectOption[] => {
	return [
		{ id: 'Passed', name: 'Passed' },
		{ id: 'Failed', name: 'Failed' },
	];
};

const getDefaultHelideckSummary = (): HelideckSummaryOverview => {
	return {
		general: '',
		remarks: '',
		dryMethodResult: '',
		wetMethodResult: '',
	};
};

const getResultActionOptions = () => {
	const RESULT_OPTIONS = [
		{ id: 'Accepted', name: 'Accepted', color: '#11ab45' },
		{ id: 'Rejected', name: 'Rejected', color: '#ff0000' },
		{ id: 'Not Detected', name: 'Not Detected', color: 'transparent' },
	];

	return RESULT_OPTIONS;
};

const getSummaryScanDataColumns = (): Column[] => {
	return [
		{ path: 'axialLength', label: 'Axial Length', content: (item: any) => <span>{getValueToDecimalPlaces(item.axialLength, 2)}</span> },
		{ path: 'circLength', label: 'Circ. Length', content: (item: any) => <span>{getValueToDecimalPlaces(item.circLength, 2)}</span>  },
		{ path: 'nominalThickness', label: 'Nominal Thickness' },
		{ path: 'corrosionAllowance', label: 'Corrosion Allowance' },
		{ path: 'minimumThickness', label: 'Minimum Thickness (mm)' },
		{ path: 'wallThickness', label: 'Wall Thickness Loss (%)' },
		{ path: 'axialLocation', label: 'Axial Location' },
		{ path: 'circLocation', label: 'Circ. Location' },
		{ path: 'maximumThickness', label: 'Maximum Thickness (mm)' },
		{ path: 'averageThickness', label: 'Average Thickness (mm)' },
	];
};

const getPECReadings = (): SelectOption[] => {
	const readings = [
		{
			id: '#000000',
			name: 0,
		},
		{
			id: '#f72f20',
			name: 2.2,
		},
		{
			id: '#fa4437',
			name: 2.4,
		},
		{
			id: '#fafa37',
			name: 3.4,
		},
		{
			id: '#37fa51',
			name: 4.1,
		},
		{
			id: '#6591f7',
			name: 5.1,
		},
		{
			id: '#2363fa',
			name: 5.5,
		},
		{
			id: '#0334a6',
			name: 5.9,
		},
	];

	return _.orderBy(readings, ['name'], ['asc']);
};

const getPECColorCodes = (): string[] => {
 	return ['#f90202', '#fe3f00', '#ff4f01', '#ff9100', '#fdbc00', '#ffcd06', '#fff800', '#1eff00', '#01ec01'];
};

const getValueIsNotEmpty = (value: unknown) => {
	// @ts-ignore
	return ![null, undefined].includes(value);
};

const getPECScanReadingColumns = (nominalThickness: number, corrosionAllowance: number, circValues: number[]): Column[] => {
	const mappedCircValues: any[] = circValues.filter((column) => getValueIsNotEmpty(column)).map((circValue) => {
		const key = circValue?.toString();

		return {
			path: key,
			label: key,
			style: (item: any) => getReadingStyle(nominalThickness, corrosionAllowance, Number(_.get(item, key)), getPECColorCodes()),
			content: (item: any) => {
				const reading = _.get(item, key);
				return <span>{getValueToDecimalPlaces(reading, 2)}</span>
			},
		};
	});

	return [ { path: 'srNo', label: 'Circ. Value' }, ...mappedCircValues ];
}

const getinspectionItemTableColumns = (): Column[] => {
	return [
		{
			path: 'inspectedItem',
			label: 'Inspected Item',
		},
		{
			path: 'observation',
			label: 'Observation / Result',
		},
		{
			path: 'remark',
			label: 'Remark',
		},
	];
};

const getRiskRatingTableColumns = (): Column[] => {
	return [
		{
			path: 'id',
			label: 'Sr No.',
		},
		{
			path: 'equipmentVesselID',
			label: 'Vessel ID',
		},
		{
			path: 'location',
			label: 'Location',
		},
		{
			path: 'observation',
			label: 'Observation',
		},
		{
			path: 'recommendation',
			label: 'Recommendation',
		},
		{
			path: 'level',
			label: 'Criticality',
			style: (item: RiskRatingItem) => getItemStyle(item),
		},
	];
};

const getGeneralInfoDummyData = (): GeneralInformationData => {
	const initialData: GeneralInformationData = {
		objectInformation: {
			acceptanceCode: '',
			testedWelds: '',
			manufactureYear: '',
			manufacturingCode: '',
			material: '',
			couplantUsed: '',
			nominalThickness: 0,
			objectSize: '',
			calibrationBlock: '',
		},
		systemSettings: {
			prf: '100',
			eht: '300',
			scanMode: 'Bidirectional',
			averagingSample: '2',
			gain: 'variable',
			pulseWidth: 100,
			digitizationRate: 5,
			lowPassFilter: 20,
			highPassFilter: 1.5,
			probeAngle: 0,
			collectionMode: 'Encoded',
			adDelay: 'variable',
			adWidth: 'variable',
		},
		maxwellEquipmentInformation: {
			instrument: 'EDDYFI',
			cableLength: 5,
			sizingAlgo: 'CWT applied if found any Isolated Corrosion',
			serialNumber: '1905716',
			insulationType: '',
			insulationThickness: '',
			footprint: '',
			model: '',
			probeSerialNumber: '',
			dateOfInspection: null,
			calibrationDueDate: null,
		}
	};

	return initialData;
};

const getUTMColorCodes = () => {
	return ["#ff0000",  "#ffff0f", "#11ab45"];
};

const getVTCoverOverview = (): CoverDataOverview[] => {
	return [
		{ report: '1. Cover Sheet', required: 'Yes', page: '3' },
		{ report: '2. Summary Sheet', required: 'Yes', page: '4' },
		{
			report: '3. Anomaly Locations (ISO), Anomaly Page',
			required: 'Yes',
			page: '6',
		},
		{
			report: '4. Photographic Report',
			required: 'Yes',
			page: 'N/A',
		},
	];
};

const getETSummaryDummyData = (): ETSummarySheetData => {
	return {
		general: '',
		remarks: '',
		inspectedItems: [],
	};
};

const getTotalValue = (item: ConcreteHammerReportItem, startRange: number, endRange: number ) => {
	const valueRange = getValueRange(startRange, endRange);
	const readingValues = valueRange.map((range) => parseInt(_.get(item, range) ?? '0'));

	const total = readingValues.reduce((a, c) => a + c, 0);
	const average = total / readingValues.length;

	return { total, average };
};

const getConcreteInspectionItemColumns = (startRange: number, endRange: number) => {
	const valueRange = getValueRange(startRange, endRange);

	const readingColumns = valueRange.map((value) => ({
		path: value.toString(),
		label: value.toString(),
		content: (item: ConcreteHammerReportItem) => _.get(item, value.toString()) ?? 'N/A'
	}));

	return [
		{
			path: 'testLocation',
			label: 'Test Location'
		},
		...readingColumns,
		{
			path: 'average',
			label: 'Average',
			content: (item: ConcreteHammerReportItem) => {
				const { average } = getTotalValue(item, startRange, endRange);
				return <span>{average.toFixed(2)}</span>
			}
		},
		{
			path: 'strength',
			label: 'Strength (Kg/Cm2)',
		},
		{
			path: 'result',
			label: 'Result'
		}
	]
};

const getLPTModalData = () => {
	return [
		{
			label: 'Sunche (384)',
			value: 'Sunche (384)',
			serialNumber: 384
		},
		{
			label: 'Sunche (385)',
			value: 'Sunche (385)',
			serialNumber: 385
		},
		{
			label: 'Sunche (386)',
			value: 'Sunche (386)',
			serialNumber: 386
		},
	];
};

const getDefectTypes = () => {
	return ['Crack', 'Slag', 'Lack of Penetration', 'Excess Penetration', 'Undercut', 'Lack of Fusion', 'Porosity', 'Linear', 'Non-Linear'];
};

const getProjectTitleOptions = () => {
	const PROJECT_TITLE_OPTIONS: SelectOption[] = [
		{
			id: 'Full Visual Inspection',
			name: 'Full Visual Inspection',
		},
		{
			id: 'NDT Inspection',
			name: 'NDT Inspection',
		},
		{
			id: 'Inspection',
			name: 'Inspection',
		},
	];

	return PROJECT_TITLE_OPTIONS;
};

const getAdditionalEquipment = () => {
	return [
		{
			value: 'Accuscan',
			label: 'Accuscan',
			serialNumber: '24/053',
		},
		{
			value: 'OMNIX4 (Evident)',
			label: 'OMNIX4 (Evident)',
			serialNumber: 'QC-0101340',
		}
	];
};

export {
	getAdditionalEquipment,
	getAnomalySummaryOptions,
	getDefectTypes,
	getNoAnomalySummaryOptions,
	getAffirmativeOptions,
	getAUTScanColumns,
	getAUTWeldColumns,
	getAcceptanceOptions,
	getCustomLPTColumns,
	getCustomFVITableColumns,
	getCustomVTTableColumns,
	getDefaultHelideckSummary,
	getExxonMobilAUTSummaryColumns,
	getETAnomalyTableColumns,
	getETSummaryDummyData,
	getCustomETSummaryColumns,
	getLPTModalData,
	getETTestingColumns,
	getVITableColumns,
	getProjectTitleOptions,
	getPipingAnomalyClassOptions,
	getConcreteInspectionItemColumns,
	getEquipmentAnomalyClassOptions,
	getCustomDRTSummaryColumns,
	getCustomLPTSummaryColumns,
	getPittingAnomalySummaryOptions,
	getPittingNoAnomalySummaryOptions,
	getCustomVTEquipmentTableColumns,
	getETWallThicknessTableColumns,
	getHelideckLineGraphData,
	getHelideckFrictionTableColumns,
	getHelideckDTColumns,
	getHelideckTDColumns,
	getHelideckVIColumns,
	getInspectionChartColors,
	getinspectionItemTableColumns,
	getGeneralInfoDummyData,
	getHelideckResultOptions,
	getLPTColumns,
	getMTIColumns,
	getWeldScanColumns,
	getMUTColumns,
	getUTMColumns,
	getNewCustomFVITableColumns,
	getRiskRatingTableColumns,
	getReviewOptions,
	getResultOptions,
	getRiskLevels,
	getRiskLevelColors,
	getRiskLevelsAsOption,
	getRTPColumns,
	getPECAnomalyColumns,
	getCustomUTMTableColumns,
	getPCNLAnomalyTableColumns,
	getPECColorCodes,
	getPECReadings,
	getPECScanColumns,
	getPECScanReadingColumns,
	getPECSectionColumns,
	getPCNLRiskAssementColumns,
	getReadingStyle,
	getResultActionOptions,
	getResultOptionsForDRTReport,
	getSummaryScanDataColumns,
	getSectionOptions,
	getAnomalyTypeClassOptions,
	getStructureAnomalyClassOptions,
	getUserTypes,
	getVTCoverOverview,
	getVTTableColumns,
	getVTEquipmentColumns,
	getInspectionTypeOptions,
	getVTStructureColumns,
	getResultItemStyle,
	getUTMColorCodes,
	getUTMReadingColumns,
	getUTMPipingInspectionOptions,
	getUTMEquipmentInspectionOptions,
	getUTMStructureInspectionOptions,
};
